import { TextArea, Button } from '@pella/sash-framework';
import AnswerButton from '../../atoms/AnswerButton';
import { answerOptions } from './answerOptions';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import styles from './QuestionAnswers.module.css';

const freeTextVisibilityLevels = {
  NOT_VISIBLE: 0,
  BUTTON_VISIBLE: 1,
  TEXTAREA_VISIBLE: 2,
};

/**
 * The follow-up question and answers
 * @param {object} props - The props for the component.
 * @param {number} props.questionNumber - Question number
 * @param {string} props.question - Question text
 * @param {import('../../pages/EquipActionPortal/questionTypes').SelectedAnswer} props.answerSelected - Whether Yes, No or neither is selected
 * @param {(answer: import('../../pages/EquipActionPortal/questionTypes').SelectedAnswer) => void} props.onSelectAnswer - On click the answer button
 * @param {string} props.additionalDetails - Additional details text
 * @param {(e: import('react').ChangeEvent<HTMLTextAreaElement>) => void} props.onChangeAdditionalDetails - On change the additional details text
 */
const QuestionAnswers = ({
  questionNumber,
  question,
  answerSelected,
  onSelectAnswer,
  additionalDetails,
  onChangeAdditionalDetails,
}) => {
  const [freeTextVisibilityLevel, setFreeTextVisibilityLevel] = useState(freeTextVisibilityLevels.NOT_VISIBLE);

  /**
   * Sets the visibility level of the free text based on the selected answer.
   * @param {import('../../pages/EquipActionPortal/questionTypes').SelectedAnswer} selectedAnswer - The currently selected answer.
   * @param {import('../../pages/EquipActionPortal/questionTypes').SelectedAnswer} answerClicked - The answer that was clicked.
   * @returns {void}
   */
  const setVisibility = (selectedAnswer, answerClicked) => {
    setFreeTextVisibilityLevel(
      selectedAnswer === answerClicked ? freeTextVisibilityLevels.NOT_VISIBLE : freeTextVisibilityLevels.BUTTON_VISIBLE,
    );
  };

  const handleOnSelectAnswerYes = () => {
    setVisibility(answerSelected, answerOptions.YES);
    onSelectAnswer(answerOptions.YES);
  };

  const handleOnSelectAnswerNo = () => {
    setVisibility(answerSelected, answerOptions.NO);
    onSelectAnswer(answerOptions.NO);
  };

  const handleOnSelectAnswerUnsure = () => {
    setVisibility(answerSelected, answerOptions.UNSURE);
    onSelectAnswer(answerOptions.UNSURE);
  };

  const onAddAdditionalDetailsClick = () => {
    setFreeTextVisibilityLevel(freeTextVisibilityLevels.TEXTAREA_VISIBLE);
  };

  const { t } = useTranslation();

  return (
    <div className={styles.questionContainer}>
      <div className={styles.questionRow}>
        <span className={styles.questionNumber}>{`${questionNumber}.`}</span>
        <span className={styles.questionText}>{question}</span>
      </div>
      <div className={styles.answersRow}>
        <AnswerButton
          isSelected={answerSelected === answerOptions.YES}
          label={t('yesButton')}
          onSelectAnswer={handleOnSelectAnswerYes}
        />
        <AnswerButton
          isSelected={answerSelected === answerOptions.NO}
          label={t('noButton')}
          onSelectAnswer={handleOnSelectAnswerNo}
        />
        <AnswerButton
          isSelected={answerSelected === answerOptions.UNSURE}
          label={t('dontKnowButton')}
          onSelectAnswer={handleOnSelectAnswerUnsure}
        />
      </div>
      {freeTextVisibilityLevel === freeTextVisibilityLevels.BUTTON_VISIBLE && (
        <div>
          <Button onClick={onAddAdditionalDetailsClick} size='sm' color='secondary'>
            {t('addAdditionalDetails')}
          </Button>
        </div>
      )}
      {freeTextVisibilityLevel === freeTextVisibilityLevels.TEXTAREA_VISIBLE && (
        <div className={styles.textareaContainer}>
          <TextArea
            id={`additional-details-${questionNumber}`}
            data-testid='additionalDetailsTextArea'
            labelText={t('additionalDetails')}
            value={additionalDetails}
            onChange={onChangeAdditionalDetails}
          />
        </div>
      )}
    </div>
  );
};

export default QuestionAnswers;
